import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Check, Trash2, X } from "lucide-react";
import { api, formatApiErrorDetail, fmtDateUS } from "@/lib/api";

const inputCls = "rounded-lg w-full bg-white/5 border border-white/15 px-3 py-2 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-primary transition-colors duration-300";
const DAY_MS = 86400000;
const toISO = (d) => d.toISOString().slice(0, 10);
const slug = (s) => s.replace(/[^a-z0-9]+/gi, "-");

const fmtTime12 = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
};

// Biweekly period containing refISO, where anchor (a payday) is always a period END date.
export const periodFor = (anchor, days, refISO) => {
  const a = new Date(`${anchor}T00:00:00Z`);
  const t = new Date(`${refISO}T00:00:00Z`);
  const k = Math.ceil((t - a) / (days * DAY_MS));
  const end = new Date(a.getTime() + k * days * DAY_MS);
  const start = new Date(end.getTime() - (days - 1) * DAY_MS);
  return { start: toISO(start), end: toISO(end) };
};

const weekday = (iso) => new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" });

export const PayrollGrid = ({ fromDate, toDate, schedule, onNavigate, onScheduleChange, onChanged }) => {
  const [grid, setGrid] = useState(null);
  const [selected, setSelected] = useState({});
  const [cell, setCell] = useState(null); // {name, date, entries}
  const [editForms, setEditForms] = useState({});
  const [payBusy, setPayBusy] = useState(false);
  const [anchorOpen, setAnchorOpen] = useState(false);
  const [anchorVal, setAnchorVal] = useState(schedule.anchor_date);

  const load = useCallback(() => {
    if (!fromDate || !toDate) return;
    api.get("/admin/payroll/grid", { params: { from_date: fromDate, to_date: toDate } })
      .then(({ data }) => setGrid(data))
      .catch(() => {});
  }, [fromDate, toDate]);
  useEffect(() => { load(); }, [load]);

  const dates = useMemo(() => {
    if (!fromDate || !toDate) return [];
    const out = [];
    for (let d = new Date(`${fromDate}T00:00:00Z`); toISO(d) <= toDate; d = new Date(d.getTime() + DAY_MS)) out.push(toISO(d));
    return out;
  }, [fromDate, toDate]);

  const shift = (n) => {
    const days = schedule.period_days * n;
    onNavigate(toISO(new Date(new Date(`${fromDate}T00:00:00Z`).getTime() + days * DAY_MS)),
               toISO(new Date(new Date(`${toDate}T00:00:00Z`).getTime() + days * DAY_MS)));
  };

  const goCurrentPeriod = () => {
    const p = periodFor(schedule.anchor_date, schedule.period_days, toISO(new Date()));
    onNavigate(p.start, p.end);
  };

  const saveAnchor = async () => {
    try {
      const { data } = await api.put("/admin/settings/pay-schedule", { anchor_date: anchorVal, period_days: schedule.period_days });
      onScheduleChange(data);
      const p = periodFor(data.anchor_date, data.period_days, toISO(new Date()));
      onNavigate(p.start, p.end);
      setAnchorOpen(false);
      toast.success(`Pay cycle saved — paydays every ${data.period_days} days`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleLunchSkip = async (date, skipped) => {
    try {
      await api.post("/admin/settings/lunch-skip", { date, skip: !skipped });
      toast.success(!skipped ? `Lunch deduction removed for ${fmtDateUS(date)} — totals updated` : `Lunch deduction back on for ${fmtDateUS(date)}`);
      load();
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const employees = grid?.employees || [];
  const pendingIds = employees.flatMap((e) => Object.values(e.cells || {}).flat().filter((en) => en.status === "pending").map((en) => en.id));
  const payable = employees.filter((e) => e.pay > 0);
  const selectedNames = payable.filter((e) => selected[e.name]).map((e) => e.name);
  const selectedTotal = payable.filter((e) => selected[e.name]).reduce((s, e) => s + e.pay, 0);

  const approve = async (ids) => {
    try {
      await api.post("/admin/time-entries/approve", { ids });
      toast.success(ids.length > 1 ? `${ids.length} entries approved` : "Entry approved — it counts toward payroll");
      load();
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openCell = (name, date) => {
    const emp = employees.find((e) => e.name === name);
    const entries = emp?.cells?.[date] || [];
    if (!entries.length) return;
    const forms = {};
    entries.forEach((en) => { forms[en.id] = { start: en.start_time || "", end: en.end_time || "", hours: String(en.hours), note: en.note || "" }; });
    setEditForms(forms);
    setCell({ name, date, entries });
  };

  const saveEntry = async (en) => {
    const f = editForms[en.id];
    const payload = { note: f.note };
    const timesChanged = f.start !== (en.start_time || "") || f.end !== (en.end_time || "");
    const hoursChanged = parseFloat(f.hours) !== en.hours;
    if (timesChanged) {
      if (!f.start || !f.end) return toast.error("Both start and end time are needed");
      if (f.end <= f.start) return toast.error("End time must be after start time");
      payload.start_time = f.start;
      payload.end_time = f.end;
    } else if (hoursChanged) {
      const h = parseFloat(f.hours);
      if (!h || h <= 0 || h > 24) return toast.error("Hours must be between 0 and 24");
      payload.hours = h;
    }
    try {
      await api.patch(`/admin/time-entries/${en.id}`, payload);
      toast.success("Entry updated");
      setCell(null);
      load();
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeEntry = async (en) => {
    if (!window.confirm(`Delete ${en.hours}h on ${fmtDateUS(en.date || cell.date)}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/time-entries/${en.id}`);
      toast.success("Entry deleted");
      setCell(null);
      load();
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const markPaid = async (names) => {
    if (!names.length) return toast.error("Select at least one worker to pay");
    const total = employees.filter((e) => names.includes(e.name)).reduce((s, e) => s + e.pay, 0);
    if (!window.confirm(`Record payment for ${names.join(", ")} — $${total.toFixed(2)} total?\n\nTheir approved hours in this period are saved to the payments ledger and won't show in future payroll totals.`)) return;
    setPayBusy(true);
    try {
      const { data } = await api.post("/admin/payroll/mark-paid", { from_date: fromDate, to_date: toDate, employees: names });
      toast.success(`${data.created} payment(s) saved — $${data.total.toFixed(2)} recorded in the ledger`);
      setSelected({});
      load();
      onChanged();
      window.dispatchEvent(new Event("payments-changed"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPayBusy(false);
    }
  };

  return (
    <div className="border-t border-white/10 pt-4 space-y-3" data-testid="payroll-grid-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-[10px] font-bold uppercase tracking-widest text-white/45">
          Timesheet grid — tap any day to edit like a spreadsheet
          {pendingIds.length > 0 && <span className="text-amber-300 ml-1" data-testid="pending-count">— {pendingIds.length} pending</span>}
        </p>
        {pendingIds.length > 0 && (
          <button onClick={() => approve(pendingIds)} data-testid="approve-all-button" className="text-[10px] font-bold uppercase tracking-widest text-primary hover:underline">
            Approve all pending
          </button>
        )}
      </div>

      <div className="flex items-center gap-1.5" data-testid="period-nav">
        <button onClick={() => shift(-1)} aria-label="Previous period" data-testid="period-prev" className="border border-white/15 rounded-lg p-2 text-white/60 hover:text-primary hover:border-primary transition-colors"><ChevronLeft size={15} /></button>
        <button onClick={goCurrentPeriod} data-testid="period-label" className="flex-1 text-center text-[11px] font-bold uppercase tracking-widest text-white/70 hover:text-primary transition-colors py-2">
          {fmtDateUS(fromDate)} – {fmtDateUS(toDate)}
        </button>
        <button onClick={() => shift(1)} aria-label="Next period" data-testid="period-next" className="border border-white/15 rounded-lg p-2 text-white/60 hover:text-primary hover:border-primary transition-colors"><ChevronRight size={15} /></button>
      </div>
      <p className="text-[10px] text-white/35">
        Paydays every {schedule.period_days} days — next one {fmtDateUS(schedule.anchor_date)} lands on a period end.{" "}
        {!anchorOpen && <button onClick={() => { setAnchorVal(schedule.anchor_date); setAnchorOpen(true); }} data-testid="paycycle-edit-open" className="text-primary hover:underline">Change pay cycle</button>}
      </p>
      {anchorOpen && (
        <div className="flex items-center gap-2" data-testid="paycycle-editor">
          <span className="text-[11px] text-white/50 shrink-0">A payday date:</span>
          <input type="date" value={anchorVal} onChange={(e) => setAnchorVal(e.target.value)} data-testid="paycycle-anchor" className={inputCls} />
          <button onClick={saveAnchor} data-testid="paycycle-save" className="bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-widest px-3.5 py-2.5 rounded-lg shrink-0">Save</button>
          <button onClick={() => setAnchorOpen(false)} aria-label="Cancel pay cycle edit" className="text-white/40 hover:text-white/70 p-1"><X size={14} /></button>
        </div>
      )}

      {!grid && <p className="text-[11px] text-white/35">Loading…</p>}
      {grid && employees.length === 0 && <p className="text-[11px] text-white/35" data-testid="grid-empty">No hours logged in this period yet — use the arrows to jump between pay periods.</p>}
      {grid && employees.length > 0 && (
        <div className="border border-white/10 rounded-lg overflow-x-auto" data-testid="payroll-grid">
          <table className="text-xs border-collapse min-w-full">
            <thead>
              <tr className="bg-white/5">
                <th className="text-left px-3 py-2 text-[9px] font-bold uppercase tracking-widest text-white/40 sticky left-0 bg-[#20241c] z-10">Date</th>
                {employees.map((emp) => (
                  <th key={emp.name} className="px-2 py-2 text-center border-l border-white/5 align-top min-w-[92px]" data-testid={`grid-col-${slug(emp.name)}`}>
                    <p className="font-bold text-white/90 text-[11px] leading-tight">{emp.name}</p>
                    <p className="text-[9px] text-white/40">${emp.rate}/h</p>
                    <p className="text-[10px] font-bold text-primary mt-0.5" data-testid={`grid-total-${slug(emp.name)}`}>{emp.net_hours}h · ${emp.pay.toFixed(2)}</p>
                    {emp.pending_hours > 0 && <p className="text-[8px] font-bold uppercase tracking-widest text-amber-300">incl. {emp.pending_hours}h pending</p>}
                    {emp.paid_pay > 0 && <p className="text-[8px] font-bold uppercase tracking-widest text-green-400" data-testid={`grid-paid-${slug(emp.name)}`}>paid ${emp.paid_pay.toFixed(2)}</p>}
                    {emp.pay > 0 && (
                      <label className="flex items-center justify-center gap-1 mt-1 text-[9px] uppercase tracking-widest text-white/50 cursor-pointer">
                        <input type="checkbox" checked={!!selected[emp.name]} onChange={(e) => setSelected({ ...selected, [emp.name]: e.target.checked })} data-testid={`grid-select-${slug(emp.name)}`} className="w-3.5 h-3.5 accent-primary" />
                        pay
                      </label>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dates.map((d) => (
                <tr key={d} className="border-t border-white/5">
                  <td className="px-3 py-1.5 whitespace-nowrap sticky left-0 bg-[#20241c] z-10">
                    <span className="font-semibold text-white/80">{fmtDateUS(d)}</span>
                    <span className="text-white/35 text-[9px] ml-1.5">{weekday(d)}</span>
                    {grid && grid.lunch_minutes > 0 && (() => {
                      const skipped = (grid.lunch_skip_dates || []).includes(d);
                      return (
                        <button onClick={() => toggleLunchSkip(d, skipped)} data-testid={`lunch-skip-${d}`}
                                title={skipped ? "Lunch deduction is off for this day — tap to put it back" : "Crew skipped lunch this day? Tap to remove the deduction"}
                                className={`block mt-0.5 text-[8px] font-bold uppercase tracking-widest transition-colors duration-200 ${skipped ? "text-amber-300 hover:text-white/70" : "text-white/30 hover:text-amber-300"}`}>
                          {skipped ? "lunch off" : `-${grid.lunch_minutes}m lunch`}
                        </button>
                      );
                    })()}
                  </td>
                  {employees.map((emp) => {
                    const entries = emp.cells?.[d] || [];
                    if (!entries.length) return <td key={emp.name} className="border-l border-white/5 px-2 py-1.5 text-center text-white/15">—</td>;
                    const allPaid = entries.every((e) => e.status === "paid");
                    const anyPending = entries.some((e) => e.status === "pending");
                    const total = Math.round(entries.reduce((s, e) => s + e.hours, 0) * 100) / 100;
                    return (
                      <td key={emp.name} onClick={() => openCell(emp.name, d)} data-testid={`grid-cell-${slug(emp.name)}-${d}`}
                          className={`border-l border-white/5 px-2 py-1.5 text-center cursor-pointer transition-colors duration-200 hover:bg-white/10 ${allPaid ? "bg-green-500/10" : anyPending ? "bg-amber-400/10" : ""}`}>
                        <p className={`font-bold ${allPaid ? "text-green-300" : "text-white/90"}`}>{total}h</p>
                        {entries.map((e) => (
                          <p key={e.id} className="text-[9px] text-white/45 leading-tight whitespace-nowrap">
                            {e.start_time ? `${fmtTime12(e.start_time)}–${fmtTime12(e.end_time)}` : `${e.hours}h`}{entries.length > 1 ? ` · ${e.job_title}` : ""}
                          </p>
                        ))}
                        {allPaid && <p className="text-[8px] font-bold uppercase tracking-widest text-green-400">paid</p>}
                        {!allPaid && anyPending && <p className="text-[8px] font-bold uppercase tracking-widest text-amber-300">pending</p>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {cell && (
        <div className="border border-primary/30 rounded-lg p-3.5 space-y-2.5" data-testid="cell-editor">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-bold uppercase tracking-widest text-primary">{cell.name} — {fmtDateUS(cell.date)}</p>
            <button onClick={() => setCell(null)} aria-label="Close editor" data-testid="cell-editor-close" className="text-white/40 hover:text-white/70 p-1"><X size={14} /></button>
          </div>
          {cell.entries.map((en) => {
            const f = editForms[en.id] || { start: "", end: "", hours: "", note: "" };
            const paid = en.status === "paid";
            return (
              <div key={en.id} className="border border-white/10 rounded-lg p-3 space-y-2" data-testid={`cell-entry-${en.id}`}>
                <p className="text-[10px] text-white/45">
                  {en.job_title} · <span className={`font-bold uppercase tracking-widest ${en.status === "pending" ? "text-amber-300" : paid ? "text-green-400" : "text-white/40"}`}>{en.status}</span>
                  {paid && " — void the payment in the ledger below to edit"}
                </p>
                {!paid && (
                  <>
                    <div className="grid grid-cols-3 gap-2">
                      <input type="time" value={f.start} onChange={(e) => setEditForms({ ...editForms, [en.id]: { ...f, start: e.target.value } })} aria-label="Start time" data-testid={`cell-start-${en.id}`} className={inputCls} />
                      <input type="time" value={f.end} onChange={(e) => setEditForms({ ...editForms, [en.id]: { ...f, end: e.target.value } })} aria-label="End time" data-testid={`cell-end-${en.id}`} className={inputCls} />
                      <input type="number" min="0" max="24" step="0.25" value={f.hours} onChange={(e) => setEditForms({ ...editForms, [en.id]: { ...f, hours: e.target.value } })} aria-label="Total hours" data-testid={`cell-hours-${en.id}`} className={inputCls} />
                    </div>
                    <p className="text-[9px] text-white/30">Change the times and hours update automatically — or just type new total hours.</p>
                    <input value={f.note} onChange={(e) => setEditForms({ ...editForms, [en.id]: { ...f, note: e.target.value } })} placeholder="Note (optional)" aria-label="Note" data-testid={`cell-note-${en.id}`} className={inputCls} />
                    <div className="flex gap-2">
                      <button onClick={() => saveEntry(en)} data-testid={`cell-save-${en.id}`} className="flex-1 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-[10px] py-2.5 rounded-lg">Save</button>
                      {en.status === "pending" && (
                        <button onClick={() => { approve([en.id]); setCell(null); }} data-testid={`cell-approve-${en.id}`} className="px-3.5 border border-primary/50 text-primary font-bold uppercase tracking-widest text-[10px] rounded-lg flex items-center gap-1"><Check size={12} /> Approve</button>
                      )}
                      <button onClick={() => removeEntry(en)} aria-label="Delete entry" data-testid={`cell-delete-${en.id}`} className="px-3 border border-red-400/40 text-red-300 rounded-lg"><Trash2 size={13} /></button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {payable.length > 0 && (
        <div className="flex gap-2" data-testid="mark-paid-actions">
          <button onClick={() => markPaid(selectedNames)} disabled={payBusy || selectedNames.length === 0} data-testid="mark-selected-paid"
                  className="flex-1 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-[11px] py-3 rounded-lg disabled:opacity-40">
            {payBusy ? "Saving..." : selectedNames.length ? `Mark ${selectedNames.length} paid — $${selectedTotal.toFixed(2)}` : "Tick “pay” on workers to pay"}
          </button>
          <button onClick={() => markPaid(payable.map((e) => e.name))} disabled={payBusy} data-testid="mark-all-paid"
                  className="px-4 border border-primary/50 text-primary font-bold uppercase tracking-widest text-[11px] rounded-lg disabled:opacity-40">
            Pay everyone
          </button>
        </div>
      )}
    </div>
  );
};
